import { Badge } from "@bowfast/ui/components/badge";
import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent } from "@bowfast/ui/components/card";
import { Separator } from "@bowfast/ui/components/separator";
import { Link, useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowRightIcon, CompassIcon, Globe2Icon, MapPinIcon, Navigation2Icon } from "lucide-react";
import { MiniRouteChart } from "~/components/routes/MiniRouteChart";
import { fadeUp } from "~/lib/animations";
import { TYPE_CONFIG } from "~/pages/routes/routes.config";
import type { RouteCardRoute } from "~/pages/routes/routes.types";

interface FeaturedRouteProps {
  readonly route: RouteCardRoute;
}

export function FeaturedRoute({ route }: FeaturedRouteProps) {
  const cfg = TYPE_CONFIG[route.type];
  const navigate = useNavigate();

  return (
    <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.04 }}>
      <button
        type="button"
        className="group block w-full cursor-pointer rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        onClick={() => navigate({ to: "/routes/$routeId", params: { routeId: route.id } })}
      >
        <Card className="relative overflow-hidden border-0 bg-abyss-900 text-white shadow-xl transition-shadow hover:shadow-2xl">
          {/* Ocean-glow overlay */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at 80% 50%, rgba(4,124,199,0.2) 0%, transparent 55%), radial-gradient(ellipse at 20% 80%, rgba(20,184,166,0.1) 0%, transparent 40%)",
            }}
            aria-hidden
          />
          <Globe2Icon
            className="pointer-events-none absolute -right-6 -top-6 size-40 rotate-12 text-white/2"
            aria-hidden
          />

          <CardContent className="relative z-10 p-5 md:p-6">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-[1fr_16rem]">
              {/* Left — route info */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Badge variant="active" className="text-[0.625rem] uppercase tracking-widest">
                    Longest route
                  </Badge>
                  <span className="text-[0.625rem] font-semibold uppercase tracking-[0.07em] text-white/40">
                    {route.distance.toLocaleString()} nm
                  </span>
                </div>

                <h2 className="font-heading text-lg font-bold leading-tight tracking-tight md:text-xl">{route.name}</h2>

                <p className="flex items-center gap-1.5 text-sm text-white/55">
                  <MapPinIcon className="size-3.5 shrink-0 text-shallow-500/70" />
                  {route.departurePort}
                  <ArrowRightIcon className="size-3 shrink-0 text-white/20" />
                  <MapPinIcon className="size-3.5 shrink-0 text-signal-500/70" />
                  {route.arrivalPort}
                </p>

                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1.5">
                    <Navigation2Icon className="size-3.5 text-white/40" />
                    <span className="font-semibold tabular-nums">{route.distance.toLocaleString()}</span>
                    <span className="text-white/40">nm</span>
                  </div>
                  <Separator orientation="vertical" className="h-4! bg-white/10" />
                  <div className="flex items-center gap-1.5">
                    <CompassIcon className="size-3.5 text-white/40" />
                    <span className="font-medium">{route.estimatedDuration}</span>
                  </div>
                  <Separator orientation="vertical" className="h-4! bg-white/10" />
                  <div className="flex items-center gap-1.5">
                    <MapPinIcon className="size-3.5 text-white/40" />
                    <span className="font-medium tabular-nums">{route.waypointCount}</span>
                    <span className="text-white/40">waypoints</span>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="border border-white/20 text-white hover:bg-white/10 hover:text-white"
                  nativeButton={false}
                  render={
                    <Link to="/routes/$routeId" params={{ routeId: route.id }} onClick={(e) => e.stopPropagation()} />
                  }
                >
                  View route
                  <ArrowRightIcon data-icon="inline-end" />
                </Button>
              </div>

              {/* Right — mini chart */}
              <div className="relative hidden h-32 overflow-hidden rounded-lg border border-white/10 bg-linear-to-br from-[#060b16] to-[#0d2040] md:block">
                <MiniRouteChart points={route.chartPoints} lineColor={cfg.lineColor} glowColor={cfg.glowColor} />
              </div>
            </div>
          </CardContent>
        </Card>
      </button>
    </motion.div>
  );
}
